import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Send,
  SmartToy,
  Person,
  Settings,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';

const MIN_WIDTH = 300;
const MAX_WIDTH = 560;
const DEFAULT_WIDTH = 380;

const PersistentChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [portalTarget, setPortalTarget] = useState(null);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: "Hi! I'm your study assistant. Ask me anything about your courses.",
      timestamp: Date.now()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [tempApiKey, setTempApiKey] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [connectionMessage, setConnectionMessage] = useState('');
  const messagesEndRef = useRef(null);
  const isDragging = useRef(false);

  // Find the portal slot in the Header
  useEffect(() => {
    const findSlot = () => {
      const el = document.getElementById('chat-toggle-slot');
      if (el && !portalTarget) setPortalTarget(el);
    };
    findSlot();
    const observer = new MutationObserver(findSlot);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [portalTarget]);

  // Push page content when sidebar opens/closes
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    root.style.transition = isDragging.current ? 'none' : 'margin-right 300ms cubic-bezier(0.4, 0, 0.2, 1)';
    root.style.marginRight = isOpen ? `${width}px` : '0px';
    return () => { root.style.marginRight = '0px'; root.style.transition = ''; };
  }, [isOpen, width]);

  // Resize via pointer events on document (not just the handle)
  const resizeRef = useRef({ startX: 0, startWidth: 0 });

  const handleResizePointerDown = useCallback((e) => {
    isDragging.current = true;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startWidth = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();

    const onMove = (ev) => {
      const dx = resizeRef.current.startX - ev.clientX;
      const w = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeRef.current.startWidth + dx));
      setWidth(w);
      const root = document.getElementById('root');
      if (root) { root.style.transition = 'none'; root.style.marginRight = `${w}px`; }
    };
    const onUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const root = document.getElementById('root');
      if (root) root.style.transition = 'margin-right 300ms cubic-bezier(0.4, 0, 0.2, 1)';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [width]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    const storedKey = localStorage.getItem('openai_api_key');
    if (storedKey) { setApiKey(storedKey); checkConnection(storedKey); }
    else setConnectionMessage('API key not configured');
  }, []);

  const checkConnection = async (key = apiKey) => {
    if (!key) { setConnectionStatus('disconnected'); setConnectionMessage('No API key provided'); return; }
    setConnectionStatus('connecting');
    setConnectionMessage('Testing connection...');
    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        method: 'GET', headers: { 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) { setConnectionStatus('connected'); setConnectionMessage('Connected to OpenAI API'); }
      else if (response.status === 401) { setConnectionStatus('error'); setConnectionMessage('Invalid API key'); }
      else { setConnectionStatus('error'); setConnectionMessage(`Connection failed: ${response.statusText}`); }
    } catch (error) { setConnectionStatus('error'); setConnectionMessage(`Connection error: ${error.message}`); }
  };

  const handleSaveApiKey = () => {
    if (tempApiKey.trim()) {
      setApiKey(tempApiKey); localStorage.setItem('openai_api_key', tempApiKey);
      checkConnection(tempApiKey); setShowApiKeyDialog(false); setTempApiKey('');
    }
  };

  const handleRemoveApiKey = () => {
    setApiKey(''); localStorage.removeItem('openai_api_key');
    setConnectionStatus('disconnected'); setConnectionMessage('API key removed'); setShowApiKeyDialog(false);
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;
    const userMessage = { id: Date.now(), type: 'user', text: inputText, timestamp: Date.now() };
    setMessages(prev => [...prev, userMessage]); setInputText(''); setIsTyping(true);
    try {
      const responseText = await generateBotResponse(inputText);
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: responseText, timestamp: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: "Sorry, I encountered an error. Please try again.", timestamp: Date.now() }]);
    }
    setIsTyping(false);
  };

  const generateBotResponse = async (userInput) => {
    if (connectionStatus !== 'connected' || !apiKey) {
      return "I need an OpenAI API key to provide intelligent responses. Click the gear icon to configure your API key.";
    }
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            { role: 'system', content: 'You are a helpful study assistant. Keep responses concise and educational.' },
            { role: 'user', content: userInput }
          ],
          max_tokens: 150, temperature: 0.7
        })
      });
      if (response.ok) { const data = await response.json(); return data.choices[0].message.content.trim(); }
      else throw new Error(`API request failed: ${response.statusText}`);
    } catch (error) {
      console.error('Error generating response:', error);
      return "I'm having trouble connecting to the AI service. Please check your connection and try again.";
    }
  };

  const formatTimestamp = (timestamp) => new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const statusColor = connectionStatus === 'connected' ? '#22c55e' : connectionStatus === 'error' ? '#ef4444' : '#94a3b8';

  const toggleButton = (
    <button
      onClick={() => setIsOpen(prev => !prev)}
      className="relative p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
      title={isOpen ? 'Close assistant' : 'Open assistant'}
      style={{ marginRight: 2 }}
    >
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        {isOpen ? (
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
        )}
      </svg>
      {/* Status dot */}
      <span
        style={{
          position: 'absolute', top: 6, right: 6,
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: statusColor,
          border: '1.5px solid white',
          boxShadow: `0 0 0 1px ${statusColor}33`,
        }}
      />
    </button>
  );

  return (
    <>
      {portalTarget && createPortal(toggleButton, portalTarget)}

      {/* Sidebar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: isOpen ? width : 0,
          zIndex: 1200,
          display: 'flex',
          transition: isDragging.current ? 'none' : 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          boxShadow: isOpen ? '-4px 0 24px rgba(0,0,0,0.08), -1px 0 6px rgba(0,0,0,0.04)' : 'none',
        }}
      >
        {/* Resize handle */}
        <div
          onPointerDown={handleResizePointerDown}
          style={{
            width: 5,
            flexShrink: 0,
            cursor: 'col-resize',
            background: 'linear-gradient(to right, #e5e7eb, #f3f4f6)',
            touchAction: 'none',
            position: 'relative',
          }}
        >
          {/* Grip dots */}
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            display: 'flex', flexDirection: 'column', gap: 3, opacity: 0.4,
          }}>
            {[0,1,2].map(i => <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#9ca3af' }} />)}
          </div>
        </div>

        {/* Chat panel */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          backgroundColor: '#fafbfc', minWidth: 0,
          borderLeft: '1px solid #e5e7eb',
        }}>
          {/* Header bar */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
              }}>
                <SmartToy sx={{ color: 'white', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.2 }}>
                  Study Assistant
                </div>
                <div style={{ fontSize: 11, color: statusColor, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusColor, display: 'inline-block' }} />
                  {connectionStatus === 'connected' ? 'Online' : connectionStatus === 'error' ? 'Error' : 'Offline'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 2 }}>
              <IconButton size="small" onClick={() => setShowApiKeyDialog(true)} sx={{ color: '#9ca3af', '&:hover': { color: '#6b7280' } }} title="Settings">
                <Settings sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: '#9ca3af', '&:hover': { color: '#6b7280' } }} title="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </IconButton>
            </div>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: 16,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: message.type === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  backgroundColor: message.type === 'user' ? '#667eea' : '#ffffff',
                  color: message.type === 'user' ? '#ffffff' : '#1a1a1a',
                  boxShadow: message.type === 'user'
                    ? '0 2px 8px rgba(102, 126, 234, 0.25)'
                    : '0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
                  fontSize: 13.5,
                  lineHeight: 1.5,
                }}>
                  <div style={{
                    fontSize: 10, opacity: 0.55, marginBottom: 4,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    {message.type === 'user' ? <Person sx={{ fontSize: 12 }} /> : <SmartToy sx={{ fontSize: 12 }} />}
                    {formatTimestamp(message.timestamp)}
                  </div>
                  {message.text}
                </div>
              </div>
            ))}

            {isTyping && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: '14px 14px 14px 4px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
                  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                }}>
                  <SmartToy sx={{ fontSize: 14, color: '#667eea' }} />
                  <span style={{ display: 'flex', gap: 3 }}>
                    {[0,1,2].map(i => (
                      <span key={i} style={{
                        width: 5, height: 5, borderRadius: '50%', backgroundColor: '#667eea',
                        animation: `typing-dot 1.2s ease-in-out ${i * 0.15}s infinite`,
                        opacity: 0.4,
                      }} />
                    ))}
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask a question..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                }}
                multiline
                maxRows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: 13.5,
                    backgroundColor: '#f9fafb',
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#d1d5db' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 1.5 },
                  },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isTyping}
                sx={{
                  minWidth: 38, height: 38, borderRadius: '10px', p: 0,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  '&:hover': { boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)' },
                  '&.Mui-disabled': { background: '#e5e7eb', boxShadow: 'none' },
                }}
              >
                <Send sx={{ fontSize: 16 }} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Typing animation keyframes */}
      <style>{`
        @keyframes typing-dot {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>

      {/* API Key Dialog */}
      <Dialog open={showApiKeyDialog} onClose={() => setShowApiKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure OpenAI API Key</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Provide your OpenAI API key to enable AI-powered responses.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Your key is stored locally and used only to communicate with OpenAI.
            </Alert>
          </Box>
          {connectionMessage && (
            <Alert severity={connectionStatus === 'connected' ? 'success' : connectionStatus === 'error' ? 'error' : 'info'} sx={{ mb: 2 }}>
              {connectionMessage}
            </Alert>
          )}
          <TextField fullWidth label="OpenAI API Key" type="password" value={tempApiKey}
            onChange={(e) => setTempApiKey(e.target.value)} placeholder="sk-..." sx={{ mb: 2 }} />
          {apiKey && (
            <Typography variant="body2" color="text.secondary">
              Current key: {apiKey.substring(0, 10)}...{apiKey.substring(apiKey.length - 4)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {apiKey && <Button onClick={handleRemoveApiKey} color="error">Remove Key</Button>}
          <Button onClick={() => setShowApiKeyDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveApiKey} variant="contained" disabled={!tempApiKey.trim()}>Save & Test</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PersistentChatBot;
